/**
 * The incremental markdown layer. This is where the render performance work
 * lives, and it is in `stream-core` rather than the web package because it is
 * pure: text in, HTML strings out, no DOM. The web package sanitises every
 * string with DOMPurify before anything reaches the document, and that step
 * is not optional there.
 *
 * The algorithm:
 *
 *   1. Split the buffer into blocks at blank lines, fence aware: a blank line
 *      inside an open code fence separates nothing.
 *   2. Every block except the last is sealed. Its parse is cached by content,
 *      so a sealed block is never reparsed no matter how many tokens follow.
 *   3. Only the open block reparses as text arrives.
 *   4. An open block that begins with a fence and has not closed it renders
 *      as escaped preformatted text, no highlighting, until the fence closes.
 *      Raw markdown never leaks into the message.
 *
 * Under a burst, the cost per token is the reparse of one small open block,
 * not the document, and the sealed prefix is byte stable so the DOM above the
 * cursor never changes.
 */

import { Marked } from 'marked';

export interface SealedBlock {
  /** Stable identity for list rendering: a hash of the block text. */
  key: string;
  /** Unsanitised HTML. The consumer must sanitise before mounting. */
  html: string;
  /** True when the block is fenced code, for lazy highlighting. */
  isCode: boolean;
  /** The raw source text, which a highlighter needs. */
  source: string;
}

export interface OpenBlock {
  html: string;
  /** True while the open block is an unterminated fence. */
  unterminatedFence: boolean;
}

export interface IncrementalResult {
  sealed: readonly SealedBlock[];
  open: OpenBlock | null;
}

export interface IncrementalRenderer {
  render(text: string): IncrementalResult;
  /** How many block parses have run. The caching tests read this. */
  readonly parseCount: number;
}

const FENCE = /^(```|~~~)/;

/** FNV-1a, enough for a stable list key. Equality is by content, not by hash. */
function hashOf(text: string): string {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/**
 * Split into blocks at blank lines, except inside a fence. Returns the block
 * texts in order. The final entry may be an unterminated fence.
 */
export function splitBlocks(text: string): string[] {
  const blocks: string[] = [];
  let current: string[] = [];
  let inFence = false;

  for (const line of text.split('\n')) {
    if (FENCE.test(line.trimStart())) {
      inFence = !inFence;
      current.push(line);
      continue;
    }
    if (line.trim() === '' && !inFence) {
      if (current.length > 0) blocks.push(current.join('\n'));
      current = [];
      continue;
    }
    current.push(line);
  }
  if (current.length > 0) blocks.push(current.join('\n'));
  return blocks;
}

/** True when the text opens a fence it never closes. */
function isUnterminatedFence(block: string): boolean {
  const lines = block.split('\n');
  const first = lines[0]?.trimStart() ?? '';
  if (!FENCE.test(first)) return false;
  let closings = 0;
  for (const line of lines.slice(1)) {
    if (FENCE.test(line.trimStart())) closings += 1;
  }
  return closings % 2 === 0;
}

export function createIncrementalRenderer(): IncrementalRenderer {
  // A local instance so options never leak between renderers or callers.
  const marked = new Marked({ gfm: true, breaks: false, async: false });
  const cache = new Map<string, SealedBlock>();
  let parseCount = 0;

  const parseSealed = (source: string): SealedBlock => {
    const cached = cache.get(source);
    if (cached !== undefined) return cached;
    parseCount += 1;
    const block: SealedBlock = {
      key: hashOf(source),
      html: marked.parse(source) as string,
      isCode: FENCE.test(source.trimStart()),
      source,
    };
    cache.set(source, block);
    return block;
  };

  return {
    get parseCount(): number {
      return parseCount;
    },

    render(text: string): IncrementalResult {
      if (text === '') return { sealed: [], open: null };

      const blocks = splitBlocks(text);
      const last = blocks[blocks.length - 1];

      // Everything before the last block is sealed and cached. The last block
      // stays open: text may still be arriving inside it.
      const sealed = blocks.slice(0, -1).map(parseSealed);

      if (last === undefined) return { sealed, open: null };

      if (isUnterminatedFence(last)) {
        // Render as code from the moment the fence opens. No parse, no
        // highlight, no raw markdown in front of the reader.
        const body = last.split('\n').slice(1).join('\n');
        return {
          sealed,
          open: {
            html: `<pre><code>${escapeHtml(body)}</code></pre>`,
            unterminatedFence: true,
          },
        };
      }

      parseCount += 1;
      return {
        sealed,
        open: { html: marked.parse(last) as string, unterminatedFence: false },
      };
    },
  };
}
